<svelte:options customElement="roi-anychart" />

<script lang="ts">
    import { writable } from 'svelte/store';
    import { onMount } from 'svelte';
    import { firstValueFrom, forkJoin, of } from 'rxjs';
    import { map } from 'rxjs/operators';
    import type { Signal, Specification } from '@bayer-int/portletce-aclibv2';
    import { SpecType } from '@bayer-int/portletce-shared';
    import { Logger } from '@bayer-int/portletce-shared';
    import { camelCase, pick } from 'lodash-es';

    import type { AcSpecId, ChartRenderProps, Config as ServiceConfig } from 'portlet/models';
    import type { RootService } from 'portlet/config/service';
    import { buildRootService } from 'portlet/config/service';
    import { defaults, View } from 'portlet/models';
    import { developmentBoot, prodBoot } from 'portlet/config/boot';
    import { isProd } from 'portlet/config/env';
    import { safeImportMetaUrl } from 'portlet/config/importMetaUrl';
    import { setLoggerContext, setServiceContext } from 'portlet/common/contexts';
    import { Attributes } from 'portlet/common/utils';
    import Lazy from 'common/components/Lazy.svelte';
    import { VegaSpecificationService } from 'portlet/services';

    type AttributeValue = string | undefined;
    interface Props {
        source?: string;
        transforms: AttributeValue;
        document: AttributeValue;
        env: AttributeValue;
        height?: AttributeValue;
        width?: AttributeValue;
    }

    let {
        source = 'nil-source',
        transforms,
        document,
        env,
        height = '100%',
        width = '100%',
    }: Props = $props();
    export const onChartRendered = () => {};
    export const onChartError = () => {};

    let component: HTMLElement | undefined = $state();

    const logger = Logger.default();
    const services = buildRootService(developmentBoot, defaults, logger);
    const cmpState = createState();
    const loggerContext = writable(logger);
    const serviceContext = writable(services);

    setLoggerContext(loggerContext);
    setServiceContext(serviceContext);

    let cmpProps = $derived(cmpState);

    onMount(() => {
        const { config, additionalAttrs } = parseAttrs();
        const rootServiceConfig: ServiceConfig = { ...defaults, env: config.env };

        // rebuild services after knowing the requested env
        const services = buildRootService(
            !isProd || config.env === 'dev' ? developmentBoot : prodBoot,
            rootServiceConfig,
            logger,
        );

        serviceContext.set(services);
        cmpState.init(services, config, recordToSignals(additionalAttrs));
    });

    function createState() {
        const { set, subscribe } = writable<null | ChartRenderProps>(null);

        const init = (
            { vegaSpecService }: RootService,
            { source, document, transforms }: Config,
            signals: Signal[],
        ) => {
            const props$ = forkJoin({
                source: source.length
                    ? forkJoin(source.map(spec => vegaSpecService.getVegaSpec(spec)))
                    : of([]),
                document: vegaSpecService.getSpecDocument(document),
                transforms: transforms.length
                    ? forkJoin(transforms.map(spec => vegaSpecService.getVegaSpec(spec)))
                    : of([]),
            }).pipe(
                map(data => ({ ...data, signals, utilities: [] })),
                map(({ source, document, transforms }): ChartRenderProps => {
                    const sourceSpec: Specification[] = source.map(spec => ({
                        type: SpecType.source,
                        source: spec,
                        options: { signals, repeats: [] },
                    }));
                    const transformSpecs: Specification[] = transforms.map(spec => ({
                        type: SpecType.transform,
                        source: spec,
                        options: { signals, repeats: [] },
                    }));
                    const specs = [...sourceSpec, ...transformSpecs];

                    return {
                        specs,
                        document,
                        documentSignals: signals,
                        utilities: [],
                        documentRepeats: [],
                        cacheInvalidationTimestamp: null,
                        documentImportMapping:
                            VegaSpecificationService.getDocumentImportMapping(document),
                        isSuspended: false,
                        disableAnimations: false,
                        viewId: View.DEFAULT_VIEW_ID,
                        filterKeyToEmitFrom: null,
                    };
                }),
            );

            return firstValueFrom(props$)
                .then(set)
                .catch(err => {
                    logger.error('Failed to fetch anychart', { source, document, transforms }, err);
                });
        };

        return { subscribe, init };
    }

    function getConfig({
        source,
        transforms,
        document,
        env: envAttr,
    }: Record<string, AttributeValue>): Config {
        if (!document) throw new Error('AnyChart.wc.svelte `document` is required');

        const importUrl = safeImportMetaUrl();
        const env = envAttr || importUrl?.searchParams.get('env');

        return {
            source: source ? source.split(',') : [],
            document,
            transforms: transforms ? transforms.split(',') : [],
            env: env === 'dev' ? 'dev' : 'prod',
        };
    }

    function parseAttrs() {
        const rootElement = component?.getRootNode();
        const attributes =
            rootElement instanceof ShadowRoot
                ? Attributes.parseNodeNamedMap(rootElement.host.attributes)
                : {};
        const ancestorAttributes = readInheritedAttributesFromAncestor();

        const props = { source, transforms, document, env };
        const config = getConfig({ ...ancestorAttributes, ...Attributes.omitUndefinedKeys(props) });

        const additionalAttrs = Attributes.attributeDifference(attributes, props);

        return { config, additionalAttrs };
    }

    /**
     * It is possible to nest <any-chart/> elements within portlets, e.g. when using HTML renderer and
     * rendering it inside another portlet. It makes sense to copy some of the configuration
     * of the ancestor portlet to the descendant. That's why we use this function to search for nearest
     * shadow root ancestor.
     */
    function readInheritedAttributesFromAncestor() {
        const rootElement = component?.getRootNode();

        if (!(rootElement instanceof ShadowRoot)) return {};

        const ancestorRootElement = rootElement.host.getRootNode();

        const ancestorAttributes =
            ancestorRootElement instanceof ShadowRoot
                ? Attributes.parseNodeNamedMap(ancestorRootElement.host.attributes)
                : {};

        const inheritableAttributes = ['env'];

        return pick(ancestorAttributes, inheritableAttributes);
    }

    function recordToSignals(obj: Record<string, string>): Signal[] {
        return Object.entries(obj).map(([name, value]) => ({
            name: camelCase(name),
            value: tryParseValue(value),
        }));
    }

    function tryParseBool(value: string): boolean | null {
        return value === 'true' || value === '' ? true : value === 'false' ? false : null;
    }

    function tryParseJSON(value: string): any | null {
        try {
            return JSON.parse(value);
        } catch (e) {
            return null;
        }
    }

    // TODO think of a way to handle non-primitives
    function tryParseValue(value: string) {
        return tryParseBool(value) ?? Attributes.parseNumber(value) ?? tryParseJSON(value) ?? value;
    }

    type Config = {
        source: AcSpecId[];
        document: AcSpecId;
        transforms: AcSpecId[];
        env: 'prod' | 'dev';
    };
</script>

<div style="height: {height}; width: {width}" bind:this={component}>
    {#if cmpProps}
        <Lazy
            lazyComponent={() => import('./AnyChart.svelte').then(module => module.default)}
            {...cmpProps}
        />
    {/if}
</div>

<style lang="scss">
    :root {
        --keep-style-tag: 1;
    }
</style>
